import { useState, useEffect, useRef, useMemo } from "react";
import type { OrderbookLevel } from "../sdk/types/data";
import {
  type EnhancementConfig,
  type EnhancedOrderbookLevel,
  type FakeLevel,
  DEFAULT_ENHANCEMENT_CONFIG,
  applyJitter,
  createFakeLevel,
  mergeFakeLevels,
  getDefaultLevelsWhenEmpty,
} from "../utils/orderbookEnhancement";

export interface RawOrderbookInput {
  asks: OrderbookLevel[];
  bids: OrderbookLevel[];
  spread: number;
  loading?: boolean;
  error?: string | null;
  isConnected: boolean;
  wsError?: string | null;
}

export interface EnhancedOrderbookOutput extends RawOrderbookInput {
  asks: EnhancedOrderbookLevel[];
  bids: EnhancedOrderbookLevel[];
}

/**
 * Decorator hook: takes raw orderbook from useOrderbook and optionally applies
 * client-side enhancements (jitter, fake pending levels). When config.enabled is false,
 * returns raw data unchanged and runs no timers.
 */
export function useEnhancedOrderbook(
  raw: RawOrderbookInput,
  config: EnhancementConfig = DEFAULT_ENHANCEMENT_CONFIG,
  maxDepth: number = 100
): EnhancedOrderbookOutput {
  const enabled = config.enabled === true;
  const jitterConfig = config.jitter ?? DEFAULT_ENHANCEMENT_CONFIG.jitter;
  const fakePendingConfig =
    config.fakePending ?? DEFAULT_ENHANCEMENT_CONFIG.fakePending;

  const [jitteredAsks, setJitteredAsks] = useState<EnhancedOrderbookLevel[]>(
    raw.asks
  );
  const [jitteredBids, setJitteredBids] = useState<EnhancedOrderbookLevel[]>(
    raw.bids
  );
  const [fakeAsks, setFakeAsks] = useState<FakeLevel[]>([]);
  const [fakeBids, setFakeBids] = useState<FakeLevel[]>([]);

  const tickRef = useRef(0);
  const nextAddTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fakeIdRef = useRef(0);
  const rawRef = useRef(raw);
  const fakePendingConfigRef = useRef(fakePendingConfig);
  const prevAmountRef = useRef<{ askTotal: number; bidTotal: number } | null>(null);
  rawRef.current = raw;
  fakePendingConfigRef.current = fakePendingConfig;

  const defaultMid = jitterConfig?.defaultMidPrice ?? 1;
  const defaultCount = jitterConfig?.defaultLevelsCount ?? 5;
  const levelsWhenEmpty = useMemo(
    () => getDefaultLevelsWhenEmpty(defaultMid, defaultCount),
    [defaultMid, defaultCount]
  );

  // Mid price from raw (best ask = lowest ask, best bid = highest bid) so jitter never crosses the book
  const midPrice = useMemo(() => {
    const askSrc = raw.asks.length > 0 ? raw.asks : levelsWhenEmpty.asks;
    const bidSrc = raw.bids.length > 0 ? raw.bids : levelsWhenEmpty.bids;
    if (askSrc.length === 0 || bidSrc.length === 0) return defaultMid;
    const bestAsk = Math.min(...askSrc.map((a) => a.price));
    const bestBid = Math.max(...bidSrc.map((b) => b.price));
    return (bestAsk + bestBid) / 2;
  }, [raw.asks, raw.bids, levelsWhenEmpty, defaultMid]);

  // Sync jittered state from raw only when raw changes enough (amount threshold) to reduce update frequency
  useEffect(() => {
    if (!enabled || !jitterConfig) return;
    const askSrc = raw.asks.length > 0 ? raw.asks : levelsWhenEmpty.asks;
    const bidSrc = raw.bids.length > 0 ? raw.bids : levelsWhenEmpty.bids;
    const askTotal = askSrc.reduce((s, l) => s + l.size, 0);
    const bidTotal = bidSrc.reduce((s, l) => s + l.size, 0);
    const prev = prevAmountRef.current;
    const thresholdPct = jitterConfig.amountChangeThresholdPct ?? 0;
    if (prev !== null && thresholdPct > 0) {
      const askDelta = prev.askTotal > 0 ? Math.abs(askTotal - prev.askTotal) / prev.askTotal : 1;
      const bidDelta = prev.bidTotal > 0 ? Math.abs(bidTotal - prev.bidTotal) / prev.bidTotal : 1;
      if (askDelta < thresholdPct && bidDelta < thresholdPct) return;
    }
    prevAmountRef.current = { askTotal, bidTotal };
    const seed = tickRef.current;
    const jitterOpts = { midPrice };
    const nextAsks = applyJitter(askSrc, jitterConfig, seed, { ...jitterOpts, side: "ask" });
    const nextBids = applyJitter(bidSrc, jitterConfig, seed, { ...jitterOpts, side: "bid" });
    const bestAskAfter = nextAsks.length ? Math.min(...nextAsks.map((a) => a.price)) : Infinity;
    const bestBidAfter = nextBids.length ? Math.max(...nextBids.map((b) => b.price)) : -Infinity;
    if (bestAskAfter <= bestBidAfter) {
      console.warn(
        "useEnhancedOrderbook: crossed order book detected after jitter; falling back to raw data."
      );
      setJitteredAsks(askSrc.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
      setJitteredBids(bidSrc.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
    } else {
      setJitteredAsks(nextAsks);
      setJitteredBids(nextBids);
    }
  }, [enabled, jitterConfig, raw.asks, raw.bids, levelsWhenEmpty, midPrice]);

  // Jitter interval: base on raw data each tick (or default levels when raw is empty).
  useEffect(() => {
    if (!enabled || !jitterConfig) return;
    const intervalMs = jitterConfig.intervalMs ?? 500;
    const id = setInterval(() => {
      tickRef.current += 1;
      const seed = tickRef.current;
      const askSrc = raw.asks.length > 0 ? raw.asks : levelsWhenEmpty.asks;
      const bidSrc = raw.bids.length > 0 ? raw.bids : levelsWhenEmpty.bids;
      const jitterOpts = { midPrice };
      const nextAsks = applyJitter(askSrc, jitterConfig, seed, { ...jitterOpts, side: "ask" });
      const nextBids = applyJitter(bidSrc, jitterConfig, seed, { ...jitterOpts, side: "bid" });
      const bestAskAfter = nextAsks.length ? Math.min(...nextAsks.map((a) => a.price)) : Infinity;
      const bestBidAfter = nextBids.length ? Math.max(...nextBids.map((b) => b.price)) : -Infinity;
      if (bestAskAfter <= bestBidAfter) {
        console.warn(
          "useEnhancedOrderbook: crossed order book detected after jitter; falling back to raw data."
        );
        setJitteredAsks(askSrc.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
        setJitteredBids(bidSrc.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
      } else {
        setJitteredAsks(nextAsks);
        setJitteredBids(nextBids);
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, jitterConfig, raw.asks, raw.bids, levelsWhenEmpty, midPrice]);

  // Fake pending: add one level per side with random delay.
  // Read config from ref so the effect can depend only on [enabled]; otherwise
  // inline config objects cause the effect to re-run every render and clear the timeout before it fires.
  const scheduleNextFake = useRef(() => {
    const cfg = fakePendingConfigRef.current;
    if (!cfg) return;
    const delay =
      cfg.minAddDelayMs +
      Math.random() * (cfg.maxAddDelayMs - cfg.minAddDelayMs);
    nextAddTimeoutRef.current = setTimeout(() => {
      nextAddTimeoutRef.current = null;
      const r = rawRef.current;
      const mid =
        r.asks.length > 0 && r.bids.length > 0
          ? (r.asks[r.asks.length - 1].price + r.bids[0].price) / 2
          : 0;
      const refSize =
        (r.asks[r.asks.length - 1]?.size ?? 0) ||
        (r.bids[0]?.size ?? 0) ||
        1;
      if (mid <= 0) {
        scheduleNextFake.current();
        return;
      }
      const cfgNow = fakePendingConfigRef.current;
      if (!cfgNow) return;
      fakeIdRef.current += 1;
      const askId = `fake-ask-${fakeIdRef.current}`;
      const bidId = `fake-bid-${fakeIdRef.current}`;
      setFakeAsks((prev) => {
        const next = [
          ...prev,
          createFakeLevel("ask", mid, refSize, cfgNow, askId),
        ].slice(-cfgNow.maxFakeLevelsPerSide);
        return next;
      });
      setFakeBids((prev) => {
        const next = [
          ...prev,
          createFakeLevel("bid", mid, refSize, cfgNow, bidId),
        ].slice(-cfgNow.maxFakeLevelsPerSide);
        return next;
      });
      scheduleNextFake.current();
    }, delay);
  });

  useEffect(() => {
    if (!enabled || !fakePendingConfig) return;
    scheduleNextFake.current();
    return () => {
      if (nextAddTimeoutRef.current) {
        clearTimeout(nextAddTimeoutRef.current);
        nextAddTimeoutRef.current = null;
      }
    };
  }, [enabled]);

  // Expire fake levels
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      const now = Date.now();
      setFakeAsks((prev) => prev.filter((f) => f.expiresAt > now));
      setFakeBids((prev) => prev.filter((f) => f.expiresAt > now));
    }, 500);
    return () => clearInterval(id);
  }, [enabled]);

  // When disabled: clear fakes and return raw
  useEffect(() => {
    if (!enabled) {
      prevAmountRef.current = null;
      setFakeAsks([]);
      setFakeBids([]);
      setJitteredAsks(raw.asks.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
      setJitteredBids(raw.bids.map((l) => ({ ...l } as EnhancedOrderbookLevel)));
    }
  }, [enabled, raw.asks, raw.bids]);

  const enhanced = useMemo((): EnhancedOrderbookOutput => {
    // When connected, never show loading so the enhanced orderbook is always on
    // (with or without raw asks/bids; fakes can still show).
    const loading = raw.isConnected ? false : (raw.loading ?? false);

    if (!enabled) {
      return {
        ...raw,
        loading,
        asks: raw.asks.map((l) => ({ ...l } as EnhancedOrderbookLevel)),
        bids: raw.bids.map((l) => ({ ...l } as EnhancedOrderbookLevel)),
      };
    }
    const asks = mergeFakeLevels(jitteredAsks, fakeAsks, maxDepth);
    const bids = mergeFakeLevels(jitteredBids, fakeBids, maxDepth);
    let spread = raw.spread;
    if (asks.length > 0 && bids.length > 0) {
      const bestAsk = asks[asks.length - 1].price;
      const bestBid = bids[0].price;
      spread = bestAsk - bestBid;
    }
    return {
      ...raw,
      loading,
      asks,
      bids,
      spread,
    };
  }, [
    enabled,
    raw,
    jitteredAsks,
    jitteredBids,
    fakeAsks,
    fakeBids,
    maxDepth,
  ]);

  return enhanced;
}
